// ============================================================================
// esign/envelope-lib.ts — client for the envelope layer of the `esign` Edge
// Function (2026-07-14). Internal (bridged-JWT) prep/send/track calls +
// public (tokenized) signer calls, plus pdfjs-dist page rendering shared by
// the prep screen and the guided signer walk.
// ============================================================================

import * as pdfjsLib from 'pdfjs-dist';
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { SUPABASE_ANON_KEY, getSupabaseToken, supabase } from '../../shared/supabase';
import {
  templateDocumentBlockedReason,
  templateDocumentRejectReason,
} from './template-document';
import type { FieldBindingOption, TemplateFieldClass } from './template-bindings';
import type { AcroFormPage, AcroFormWidget } from './acroform-placement';
import { EsignError, esignInternal, esignPublic } from './lib';
import { errorMessage } from '../../shared/errors';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

const ESIGN_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/esign`;

// ── types (mirror envelope.ts row shapes) ───────────────────────────────────

export type RecipientRole = 'SIGNER' | 'APPROVER' | 'CC';
export type FieldType =
  | 'signature' | 'initials' | 'name' | 'email' | 'date' | 'text' | 'checkbox';

export interface EnvelopeRow {
  envelope_id: string;
  subject_type: 'contract' | 'proposal' | 'document';
  subject_id: string | null;
  title: string;
  status: 'Draft' | 'Sent' | 'In_Progress' | 'Completed' | 'Declined' | 'Voided';
  created_by_eid: string | null;
  source_pdf_url: string | null;
  sealed_pdf_url: string | null;
  document_hash: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  // Sender-authored copy + delivery settings (Phase 2). NULL ⇒ default.
  note_to_signer: string | null;
  email_subject: string | null;
  email_body: string | null;
  reminder_every_days: number | null;
  expires_in_days: number | null;
  /** Resolved display name of the creator, supplied by env_status. */
  sender_name?: string | null;
  // AFB-0075 / PR 5 — provenance for an envelope built from an onboarding
  // packet. All three are NULL on every envelope built by hand, which is the
  // test the review panel uses: no packet_id, no packet review.
  /** The esign_packets row this was assembled from. */
  packet_id?: string | null;
  /** The employee_onboarding row the merge values were resolved from. */
  source_onboarding_id?: string | null;
  /** { added, removed } — the per-send document override, or NULL for none. */
  document_overrides?: unknown;
}

export interface RecipientRow {
  recipient_id: string;
  envelope_id: string;
  email: string;
  name: string | null;
  role: RecipientRole;
  signing_order: number;
  status: 'Pending' | 'Notified' | 'Viewed' | 'Signed' | 'Declined';
  signed_at: string | null;
  /** Identity policy. Defaults to today's delivery-based verification. */
  auth_mode?: 'emailed_link' | 'email_otp' | 'sms_otp';
  /**
   * Which template role this person was resolved from, when the envelope came
   * from one. NOT a column on esign_recipients today — role resolution lands in
   * a later PR of AFB-0075 — so it is absent on every envelope built by hand,
   * and captureLayout records null for those rather than inventing a role.
   */
  role_key?: string | null;
}

export interface FieldRow {
  field_id: string;
  envelope_id: string;
  recipient_id: string;
  field_type: FieldType;
  page: number;
  position_x: number;
  position_y: number;
  width: number;
  height: number;
  required: boolean;
  /**
   * NULL for every restricted field, always — esign_fields carries
   * CHECK (NOT (is_sensitive AND value IS NOT NULL)), so the readable table is
   * incapable of holding one. The answer lives in esign_field_secrets and is
   * never fetched by these screens.
   */
  value: string | null;
  /** Restricted to HR and executives. See TemplateField.is_sensitive. */
  is_sensitive?: boolean | null;
}

export interface MintResult {
  recipient_id: string;
  email: string;
  delivery: { delivered: boolean; channel: string; reason?: string };
  signing_url?: string;
}

export interface EnvStatus {
  envelope: EnvelopeRow;
  recipients: RecipientRow[];
  fields: FieldRow[];
  signing_order: number | null;
}

/** Signer-facing meta for envelope-scoped tokens. */
export interface EnvSignerMeta {
  scope: 'envelope';
  state: 'active' | 'verified' | 'completed' | 'waiting';
  envelope_id: string;
  title: string;
  envelope_status: string;
  recipient_role: RecipientRole;
  recipient_status: string;
  signing_order: number;
  current_group: number | null;
  my_turn: boolean;
  signer_email_masked: string;
  signer_name: string | null;
  verified: boolean;
  otp_pending: boolean;
  // 2026-07-28 UX rebuild — instruction panel + consent gate inputs.
  /** Sender's note / signing instructions. Rendered above the document. */
  note_to_signer: string | null;
  /** Display name of whoever sent the envelope. */
  sender_name: string | null;
  /** Signing-link expiry, ISO-8601. */
  expires_at: string | null;
  /** True once this signer has passed the ESIGN/UETA consent gate. */
  consented: boolean;
  /** Version stamp of the disclosure text the signer was shown. */
  disclosure_version: string | null;
  auth_mode: 'emailed_link' | 'email_otp' | 'sms_otp';
  fields: Array<Pick<FieldRow,
    'field_id' | 'field_type' | 'page' | 'position_x' | 'position_y' |
    'width' | 'height' | 'required' | 'value'>>;
}

// ── sender-authored copy + reusable templates (Phase 2) ─────────────────────

// AFB-0075 / PR 2 — the CHECK on esign_templates.document_type now allows the
// onboarding vocabulary as well as the three sales agreements. Kept in the same
// order as the constraint so the two can be diffed by eye.
export type EsignDocumentType =
  | 'client_service_agreement'
  | 'building_service_agreement'
  | 'subcontractor_agreement'
  | 'other'
  | 'welcome_letter'
  | 'personal_information'
  | 'handbook'
  | 'direct_deposit'
  | 'payment_calendar'
  | 'offer_letter'
  | 'w9'
  | 'state_withholding'
  | 'i9'
  | 'federal_withholding';

/** Every allowed document_type, in the order a person should be offered them. */
export const DOCUMENT_TYPE_LABELS: Record<EsignDocumentType, string> = {
  client_service_agreement: 'Client service agreement',
  building_service_agreement: 'Building service agreement',
  subcontractor_agreement: 'Subcontractor agreement',
  offer_letter: 'Offer letter',
  welcome_letter: 'Welcome letter',
  personal_information: 'Personal information',
  handbook: 'Handbook',
  direct_deposit: 'Direct deposit',
  payment_calendar: 'Payment calendar',
  i9: 'I-9',
  federal_withholding: 'Federal withholding (W-4)',
  state_withholding: 'State withholding',
  w9: 'W-9',
  other: 'Other',
};

/**
 * Which employee_onboarding checklist item this template satisfies, if any.
 * These are keys in checklist_json's "documents" group, not free text — on
 * completion the write-back flips the matching item, so a typo would flip
 * nothing and say nothing.
 */
export const CHECKLIST_KEY_LABELS: Record<string, string> = {
  offer_letter_signed: 'Offer letter signed',
  i9: 'I-9',
  w4: 'W-4',
  w9: 'W-9',
  direct_deposit: 'Direct deposit',
  handbook_signed: 'Handbook signed',
};

/** One role PLACEHOLDER on a template. Never a person — see recipient_roles. */
export interface TemplateRecipientRole {
  role_key: string;
  label: string;
  role: RecipientRole;
  signing_order: number;
  auth_mode: 'emailed_link' | 'email_otp';
}

/** One placed field, normalised so a layout can be reapplied to any envelope. */
export interface TemplateField {
  recipient_index: number;
  field_type: FieldType;
  page: number;
  position_x: number;
  position_y: number;
  width: number;
  height: number;
  required: boolean;
  anchor_string?: string | null;
  // AFB-0075 / PR 2. Optional on READ because every layout captured before this
  // PR was written without them: a missing field_class is a SIGNER field
  // (normaliseFieldTagging), so an old layout keeps meaning what it meant.
  field_class?: TemplateFieldClass | null;
  /** Only ever set when field_class is 'merge'. An active esign_field_bindings key. */
  binding?: string | null;
  /** Which recipient_roles entry fills this. recipient_id is envelope-scoped and never stored here. */
  role_key?: string | null;
  /**
   * RESTRICTED HR FIELD — SSN / DOB / bank account / routing (2026-08-20).
   *
   * Always a SIGNER field with a null binding: the company never prefills one.
   * esign_instantiate_packet_envelope carries this onto esign_fields, which
   * forces the same two things at the database and routes the signer's answer
   * to esign_field_secrets. Optional on READ because layouts written before
   * this exist; absent reads as false.
   */
  is_sensitive?: boolean | null;
}

export interface EsignTemplate {
  template_id: string;
  name: string;
  document_type: EsignDocumentType;
  email_subject: string | null;
  email_body: string | null;
  note_to_signer: string | null;
  field_layout: TemplateField[] | null;
  is_active: boolean;
  created_by_eid: string | null;
  // AFB-0075 / W-09 — the document, at last. METADATA ONLY: the bytes live in
  // esign_template_documents, one row per template, so listTemplates()'s
  // select('*') stays cheap on every load of the composer and the prep screen.
  // A non-null document_bytes is the test for "this template carries its own
  // PDF" — it and the child row are written in one transaction by
  // esign_save_template_document, so they cannot disagree.
  document_filename: string | null;
  document_bytes: number | null;
  document_hash: string | null;
  hash_algo: string | null;
  /** Provenance only. NOT where the document is read from — see saveTemplateDocument. */
  source_envelope_id: string | null;
  // AFB-0075 / PR 2 — what a packet needs to know about a member document.
  /**
   * Pages in the saved PDF, PARSED at upload and never typed. NULL means the
   * document's length is unknown, which disqualifies the template from a packet:
   * assembly offsets every later document's field pages by this number.
   */
  page_count: number | null;
  /** employee_onboarding checklist item this satisfies, or NULL for none. */
  checklist_key: string | null;
  /** Role placeholders resolved to real people at send time. Never emails. */
  recipient_roles: TemplateRecipientRole[] | null;
}

// The rules about a template document are pure and live in ./template-document,
// which is testable — this module imports pdfjs-dist at load time and therefore
// cannot be imported by a node-environment test at all. Re-exported so callers
// have one import site.
export {
  MAX_TEMPLATE_DOCUMENT_BYTES,
  formatDocumentSize,
  templateDocumentBlockedReason,
  templateDocumentRejectReason,
  templateHasDocument,
} from './template-document';

// Same arrangement for the authoring rules (AFB-0075 / PR 2): pure and tested
// in ./template-bindings, re-exported here so a component has one import site.
export {
  fieldTaggingGap,
  markFieldRestricted,
  normaliseFieldTagging,
  selectableBindings,
  templateIsPacketReady,
  templatePacketBlockers,
  untaggedMergeFields,
} from './template-bindings';
export type {
  FieldBindingOption,
  FieldTagging,
  TemplateFieldClass,
} from './template-bindings';

// And again for AFB-0075 / PR 4 (2026-08-20): reading a layout out of the PDF's
// own AcroForm is arithmetic plus a ruling, so all of it is pure and tested in
// ./acroform-placement. Only the pdf.js extraction below lives on this side.
export {
  NO_SIGNER_BLOCKER,
  SKIP_REASONS,
  isMeaninglessFieldName,
  isSensitiveFieldName,
  placeAcroFormFields,
} from './acroform-placement';
export type {
  AcroFormPage,
  AcroFormPlacementInput,
  AcroFormPlacementResult,
  AcroFormWidget,
  SkippedGroup,
} from './acroform-placement';

/** Sender-authored copy + delivery settings held on the envelope. */
export interface EnvelopeCopy {
  email_subject: string | null;
  email_body: string | null;
  note_to_signer: string | null;
  reminder_every_days: number | null;
  expires_in_days: number | null;
}

export async function listTemplates(): Promise<EsignTemplate[]> {
  const { data, error } = await supabase
    .from('esign_templates')
    .select('*')
    .eq('is_active', true)
    .order('document_type')
    .order('name');
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as EsignTemplate[];
}

export async function saveTemplate(input: {
  name: string;
  document_type: EsignDocumentType;
  email_subject?: string | null;
  email_body?: string | null;
  note_to_signer?: string | null;
  field_layout?: TemplateField[] | null;
  created_by_eid?: string | null;
}): Promise<EsignTemplate> {
  const { data, error } = await supabase
    .from('esign_templates')
    .insert(input as never)
    .select()
    .single();
  if (error) throw new Error(error.message);
  return data as unknown as EsignTemplate;
}

/**
 * Persist the sender's copy on the envelope. A plain update — none of these
 * are protected columns, and RLS already restricts writes to employees.
 */
export async function saveEnvelopeCopy(
  envelope_id: string,
  copy: Partial<EnvelopeCopy>,
): Promise<void> {
  const { error } = await supabase
    .from('esign_envelopes')
    .update(copy as never)
    .eq('envelope_id', envelope_id);
  if (error) throw new Error(error.message);
}

/**
 * Correct one field's prefilled value before the envelope is sent.
 *
 * AFB-0075 / PR 5. This is the write behind "every merge value shown and
 * editable" on the packet review: the resolver's answer is a starting point,
 * not a fact, and the person about to send it is the one who can see that the
 * address is out of date. Blank is written as NULL so an emptied field reads as
 * unresolved everywhere — the send gate tests for a value, and '' would pass a
 * test it should fail.
 */
export async function setFieldValue(
  field_id: string,
  value: string | null,
): Promise<void> {
  const trimmed = (value ?? '').trim();
  const { error } = await supabase
    .from('esign_fields')
    .update({ value: trimmed === '' ? null : trimmed } as never)
    .eq('field_id', field_id);
  if (error) throw new Error(error.message);
}

/**
 * Correct who a recipient is before anything is sent.
 *
 * The packet addresses itself from the employee record, which can be wrong or
 * stale. Fixing it here changes THIS envelope only — the employee record is not
 * touched, because a typo on one packet is not evidence about the person.
 */
export async function saveRecipientContact(
  recipient_id: string,
  contact: { name: string | null; email: string },
): Promise<void> {
  const { error } = await supabase
    .from('esign_recipients')
    .update({
      name: (contact.name ?? '').trim() || null,
      email: contact.email.trim().toLowerCase(),
    } as never)
    .eq('recipient_id', recipient_id);
  if (error) throw new Error(error.message);
}

/** Flip a placed field between required and optional. */
export async function setFieldRequired(
  field_id: string,
  required: boolean,
): Promise<void> {
  const { error } = await supabase
    .from('esign_fields')
    .update({ required } as never)
    .eq('field_id', field_id);
  if (error) throw new Error(error.message);
}

/**
 * Snapshot the current placements as a reusable layout. Fields are keyed by
 * recipient INDEX rather than recipient_id, so the layout can be applied to a
 * different envelope with a different set of people in the same roles.
 *
 * AFB-0075 / PR 2 — capture stays HONEST. What a live envelope holds is
 * geometry: where a box is and who it belongs to. It says nothing about whether
 * a box should be prefilled from the database, so every captured entry is
 * written as a SIGNER field with a null binding. Guessing "this looks like a
 * name field, bind it to employee.name_full" would put a value the sender never
 * chose onto a document somebody then signs; merge bindings are tagged by hand
 * on the template, and the prep screen says so after a save.
 */
export function captureLayout(
  recipients: RecipientRow[],
  fields: FieldRow[],
): TemplateField[] {
  const signers = recipients.filter((r) => r.role !== 'CC');
  const order = signers.map((r) => r.recipient_id);
  return fields
    .filter((f) => order.includes(f.recipient_id))
    .map((f) => {
      const index = order.indexOf(f.recipient_id);
      return {
        recipient_index: index,
        field_type: f.field_type,
        page: f.page,
        position_x: f.position_x,
        position_y: f.position_y,
        width: f.width,
        height: f.height,
        required: f.required,
        field_class: 'signer' as const,
        binding: null,
        // Carried through when the envelope was built from a template and the
        // recipient still knows which role it came from; null otherwise.
        role_key: signers[index]?.role_key ?? null,
        // Carried, NOT re-derived. Dropping it here would make "capture the
        // layout, apply it to the next hire" a silent downgrade: the box over
        // the SSN line would come back as an ordinary signer field and the
        // answer would land in the readable column.
        is_sensitive: f.is_sensitive === true,
      };
    });
}

/**
 * Apply a saved layout to this envelope. Placements whose recipient index has
 * no matching recipient are skipped and reported rather than silently
 * dropped — a three-signer layout applied to a two-signer envelope should
 * say so.
 */
export async function applyLayout(
  envelope_id: string,
  recipients: RecipientRow[],
  layout: TemplateField[],
): Promise<{ placed: number; skipped: number }> {
  const signers = recipients.filter((r) => r.role !== 'CC');
  let placed = 0;
  let skipped = 0;
  for (const f of layout) {
    const target = signers[f.recipient_index];
    if (!target) { skipped += 1; continue; }
    await envPlaceField({
      envelope_id,
      recipient_id: target.recipient_id,
      field_type: f.field_type,
      page: f.page,
      position_x: f.position_x,
      position_y: f.position_y,
      width: f.width,
      height: f.height,
      required: f.required,
      // The other half of the carry-through in captureLayout. A layout entry
      // that says "restricted" must place a restricted field, or applying a
      // template would be the way round the rule.
      is_sensitive: f.is_sensitive === true,
    });
    placed += 1;
  }
  return { placed, skipped };
}

/** One row of the e-sign audit trail. */
export interface EsignEvent {
  event_id: string;
  envelope_id: string | null;
  recipient_id: string | null;
  token_id: string | null;
  action: string;
  signer_email: string | null;
  signer_name: string | null;
  ip_address: string | null;
  user_agent: string | null;
  detail: Record<string, unknown> | null;
  occurred_at: string;
}

/**
 * One returned restricted answer — an SSN, date of birth, bank account or
 * routing number the employee typed in. These never live in `esign_fields`
 * (a CHECK constraint makes that impossible) and this is the ONLY way to read
 * them: `esign_envelope_secrets` raises TCS_ESIGN_RESTRICTED for anybody
 * outside `can_view_restricted_hr()`.
 */
export interface EsignFieldSecret {
  field_id: string;
  field_type: string;
  page: number;
  value: string;
}

/**
 * Read the restricted answers for one envelope.
 *
 * Fails CLOSED and loudly: the RPC raises rather than returning an empty set
 * when the caller is not allowed, so an empty array here means "there are
 * none", never "you may not see them". Do not add a catch that flattens the
 * two — that distinction is the whole point of the gate.
 */
export async function listEnvelopeSecrets(
  envelope_id: string,
): Promise<EsignFieldSecret[]> {
  const { data, error } = await supabase.rpc('esign_envelope_secrets', {
    p_envelope_id: envelope_id,
  });
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as EsignFieldSecret[];
}

/** Chain of custody for one envelope, oldest first. */
export async function listEnvelopeEvents(
  envelope_id: string,
): Promise<EsignEvent[]> {
  const { data, error } = await supabase
    .from('contract_signature_events')
    .select('*')
    .eq('envelope_id', envelope_id)
    .order('occurred_at', { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as EsignEvent[];
}

/** Set a recipient's identity policy (DR-01 / SMS readiness). */
export async function setRecipientAuthMode(
  recipient_id: string,
  auth_mode: 'emailed_link' | 'email_otp',
): Promise<void> {
  const { error } = await supabase
    .from('esign_recipients')
    .update({ auth_mode } as never)
    .eq('recipient_id', recipient_id);
  if (error) throw new Error(error.message);
}

/** Attach a field layout to an existing template. */
export async function saveLayoutToTemplate(
  template_id: string,
  field_layout: TemplateField[],
): Promise<void> {
  const { error } = await supabase
    .from('esign_templates')
    .update({ field_layout } as never)
    .eq('template_id', template_id);
  if (error) throw new Error(error.message);
}

// ── AFB-0075 / W-09: the document on the template ───────────────────────────
//
// Antonela's ask was "save templates like DocuSign … I already have my common
// documents ready to go". Templates already carried the covering email and the
// field layout; what they had no column for was THE DOCUMENT, so every send
// still began with a file picker.
//
// The bytes are held base64 in esign_template_documents rather than as a storage
// URL, for two measured reasons:
//
//   1. The app cannot reach storage. Both e-sign buckets are private and
//      storage.objects has NO RLS policies at all, so every byte moves through
//      the esign-internal Edge Function as service_role. A URL on the template
//      would name an object this code can neither read nor write.
//   2. Re-fetching the PDF from the envelope the template came from is a TRAP:
//      env_pdf serves the SEALED document once that envelope completes. Since
//      templates are captured from documents that then get signed, the second
//      use of any template would hand back a copy with somebody's signature
//      burned in. That is the main path, not an edge case.
//
/** SHA-256 hex of the bytes, matching esign_envelopes' hash_algo default. */
export async function sha256Hex(bytes: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Store (or replace) the reusable document on a template.
 *
 * One RPC, because the bytes and the metadata are two rows: a client that wrote
 * one without the other would leave a template advertising a document it does
 * not hold, and the hash — the only witness — lives on the side that might not
 * have been written. The direct table grants are revoked; this is the only way
 * in.
 */
export async function saveTemplateDocument(
  template_id: string,
  file: File,
): Promise<void> {
  const reject = templateDocumentRejectReason(file);
  if (reject) throw new Error(reject);

  const bytes = await file.arrayBuffer();

  // Page count is PARSED, never typed — and it is parsed BEFORE anything is
  // written, because a document whose length we cannot read must not reach the
  // table at all. See parsePdfPageCount for why that is a refusal and not a
  // warning.
  const page_count = await parsePdfPageCount(bytes, file.name);

  const [pdf_base64, hash] = await Promise.all([
    fileToBase64(file),
    sha256Hex(bytes),
  ]);
  const { error } = await supabase.rpc('esign_save_template_document', {
    p_template_id: template_id,
    p_pdf_base64: pdf_base64,
    p_filename: file.name,
    p_byte_size: file.size,
    // Hashed from the file's own bytes, not from the base64 — so the hash is
    // about the document and not about one encoding of it.
    p_document_hash: hash,
  });
  if (error) throw new Error(error.message);

  // Second call by design: adding a sixth argument to the document RPC would
  // either orphan the live UI or create a PostgREST overload (see the migration
  // comment). It runs AFTER the document RPC succeeds, so page_count can never
  // describe a document that failed to save.
  try {
    await setTemplatePageCount(template_id, page_count);
  } catch (e) {
    throw new Error(
      `The document saved, but its page count did not: ` +
        `${errorMessage(e, 'the page count could not be written')}. This template cannot ` +
        `go in a packet until the document is saved again.`,
    );
  }
}

/**
 * Pages in a PDF, read from the bytes with the pdfjs this module already loads.
 *
 * Throws — deliberately, with the whole upload behind it. A template whose page
 * count is unknown cannot safely be a packet member: assembly offsets every
 * later document's field pages by this number, so a missing or guessed value
 * silently lands signature boxes on the wrong page of a document somebody then
 * signs. Refusing the upload leaves the sender with a file to fix; saving it
 * anyway leaves them with a template that looks fine and is not.
 */
export async function parsePdfPageCount(
  bytes: ArrayBuffer,
  filename: string,
): Promise<number> {
  let pages: number;
  try {
    // A copy: pdf.js transfers the buffer it is handed to its worker, which
    // would detach the one the caller still needs for the hash.
    const task = pdfjsLib.getDocument({ data: bytes.slice(0) });
    pages = (await task.promise).numPages;
    // Nothing is rendered from this document — the worker can go straight back.
    await task.destroy();
  } catch (e) {
    throw new Error(
      `Could not read "${filename}" as a PDF, so its page count is unknown — nothing was ` +
        `saved. A template with an unknown page count cannot go in a packet, because ` +
        `assembly shifts every later document's fields by it. Re-export the file as a PDF ` +
        `and try again. (${errorMessage(e, 'unreadable')})`,
    );
  }
  if (!Number.isInteger(pages) || pages <= 0) {
    throw new Error(
      `"${filename}" reports ${pages} pages, which cannot be right — nothing was saved. ` +
        `Re-export the file as a PDF and try again.`,
    );
  }
  return pages;
}

/**
 * Set (or clear, with null) the parsed page count. A separate SECURITY DEFINER
 * RPC because the direct column write is not granted.
 */
export async function setTemplatePageCount(
  template_id: string,
  page_count: number | null,
): Promise<void> {
  const { error } = await supabase.rpc('esign_set_template_page_count', {
    p_template_id: template_id,
    p_page_count: page_count,
  });
  if (error) throw new Error(error.message);
}

/** Every value a merge field is allowed to pull. Active rows only. */
export async function listFieldBindings(): Promise<FieldBindingOption[]> {
  const { data, error } = await supabase
    .from('esign_field_bindings')
    .select('binding_key, label, is_active, is_sensitive, notes')
    .eq('is_active', true)
    .order('binding_key');
  if (error) throw new Error(error.message);
  return (data ?? []) as FieldBindingOption[];
}

/**
 * The two pieces of template metadata a person sets by hand: what kind of
 * document this is, and which onboarding checklist item it satisfies.
 * page_count is deliberately NOT here — it is parsed, never typed.
 */
export async function saveTemplateMeta(
  template_id: string,
  meta: { document_type: EsignDocumentType; checklist_key: string | null },
): Promise<void> {
  const { error } = await supabase
    .from('esign_templates')
    .update(meta as never)
    .eq('template_id', template_id);
  if (error) throw new Error(error.message);
}

/**
 * Replace the role placeholders on a template.
 *
 * Emails are NEVER stored here: a template is reusable precisely because it does
 * not know who it is for. An empty list is written as NULL so "no roles set up"
 * has one representation rather than two.
 */
export async function saveTemplateRoles(
  template_id: string,
  roles: TemplateRecipientRole[],
): Promise<void> {
  const { error } = await supabase
    .from('esign_templates')
    .update({ recipient_roles: roles.length > 0 ? roles : null } as never)
    .eq('template_id', template_id);
  if (error) throw new Error(error.message);
}

/** Refresh a single template after a document change, without a full reload. */
export async function getTemplate(template_id: string): Promise<EsignTemplate | null> {
  const { data, error } = await supabase
    .from('esign_templates')
    .select('*')
    .eq('template_id', template_id)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return (data as unknown as EsignTemplate) ?? null;
}

/**
 * Remove the document from a template. Clears the bytes AND the metadata —
 * including page_count, which describes a document that no longer exists.
 * Leaving it behind would let a template with no PDF still look measurable to
 * packet assembly.
 */
export async function clearTemplateDocument(template_id: string): Promise<void> {
  const { error } = await supabase.rpc('esign_save_template_document', {
    p_template_id: template_id,
    p_pdf_base64: null,
    p_filename: null,
    p_byte_size: null,
    p_document_hash: null,
  });
  if (error) throw new Error(error.message);
  try {
    await setTemplatePageCount(template_id, null);
  } catch (e) {
    throw new Error(
      `The document was removed, but the old page count is still on the template: ` +
        `${errorMessage(e, 'the page count could not be cleared')}. Reload and remove it again.`,
    );
  }
}

/**
 * The base64 PDF saved against a template.
 *
 * Deliberately loud in both failure modes. A template with no document must not
 * quietly produce a blank envelope — that is the defect Antonela is already
 * living with, one layer deeper — and metadata that claims a document whose
 * bytes are gone should be unreachable (esign_save_template_document writes
 * both sides in one transaction), which is exactly why it is reported rather
 * than swallowed.
 */
export async function loadTemplateDocumentBase64(
  template: EsignTemplate,
): Promise<string> {
  const blocked = templateDocumentBlockedReason(template);
  if (blocked) throw new Error(blocked);
  const { data, error } = await supabase
    .from('esign_template_documents')
    .select('pdf_base64')
    .eq('template_id', template.template_id)
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data?.pdf_base64) {
    throw new Error(
      `"${template.name}" says it has a document but the file is missing. ` +
        `Re-save the document to the template.`,
    );
  }
  return data.pdf_base64 as string;
}

/**
 * Start a new envelope from a template: the template's document, its covering
 * email, and its note to the signer, in one action.
 *
 * The field layout is NOT applied here. Fields are bound to recipients by index
 * and the new envelope has none yet, so applying the layout now would place
 * every field against nobody. The prep screen already applies a layout once
 * recipients exist (field-tools → applyLayout), and that is where it belongs.
 *
 * Deliberately loud when the document is missing. A template with no PDF
 * silently producing a blank envelope is the failure Antonela is already living
 * with, one layer deeper.
 */
export async function createEnvelopeFromTemplate(input: {
  template: EsignTemplate;
  title: string;
  subject_type?: string;
  subject_id?: string;
  /**
   * A PDF the caller is supplying instead. Used when the template carries only
   * boilerplate (no saved document) and the sender picked a file — the covering
   * email is still worth reusing, which is why that case goes through here
   * rather than round the side.
   */
  pdfBase64Override?: string;
}): Promise<EnvelopeRow> {
  const { template, title, subject_type, subject_id, pdfBase64Override } = input;

  let pdf_base64 = pdfBase64Override;

  if (!pdf_base64) {
    pdf_base64 = await loadTemplateDocumentBase64(template);
  }

  const res = await envCreate({
    title,
    subject_type,
    subject_id,
    pdf_base64,
  });

  // Copy the sender's boilerplate. Failing here must not lose the envelope —
  // the document is already uploaded and the user can retype a subject line, but
  // they cannot retype a PDF.
  const copy: Partial<EnvelopeCopy> = {
    email_subject: template.email_subject,
    email_body: template.email_body,
    note_to_signer: template.note_to_signer,
  };
  if (copy.email_subject || copy.email_body || copy.note_to_signer) {
    try {
      await saveEnvelopeCopy(res.envelope.envelope_id, copy);
    } catch (e) {
      console.error('[esign] template copy did not apply', e);
    }
  }

  return res.envelope;
}

// ── internal actions ────────────────────────────────────────────────────────

export function envCreate(input: {
  title: string;
  subject_type?: string;
  subject_id?: string;
  pdf_base64?: string;
}): Promise<{ ok: boolean; envelope: EnvelopeRow }> {
  return esignInternal({ action: 'env_create', ...input });
}

export function envAddRecipient(input: {
  envelope_id: string;
  email: string;
  name?: string;
  role: RecipientRole;
  signing_order: number;
}): Promise<{ ok: boolean; recipient: RecipientRow }> {
  return esignInternal({ action: 'env_add_recipient', ...input });
}

export function envPlaceField(input: {
  envelope_id: string;
  recipient_id: string;
  field_type: FieldType;
  page: number;
  position_x: number;
  position_y: number;
  width: number;
  height: number;
  required?: boolean;
  /** Restricted to HR and executives; the answer never touches esign_fields.value. */
  is_sensitive?: boolean;
}): Promise<{ ok: boolean; field: FieldRow }> {
  return esignInternal({ action: 'env_place_field', ...input });
}

export function envRemoveField(field_id: string): Promise<{ ok: boolean }> {
  return esignInternal({ action: 'env_remove_field', field_id });
}

export function envSend(envelope_id: string): Promise<{
  ok: boolean; status: string; signing_order: number; minted: MintResult[];
}> {
  return esignInternal({ action: 'env_send', envelope_id });
}

export function envAdvance(envelope_id: string, force_remint = false): Promise<{
  ok: boolean; completed: boolean; signing_order?: number; minted: MintResult[];
}> {
  return esignInternal({ action: 'env_advance', envelope_id, force_remint });
}

export function envStatus(envelope_id: string): Promise<EnvStatus> {
  return esignInternal({ action: 'env_status', envelope_id });
}

// ── lifecycle (RPCs, 2026-07-15) ────────────────────────────────────────────
// Policy: in-flight envelopes are VOIDED (links die immediately); Draft and
// Voided envelopes can be DELETED (cascade w/ deletion_requests snapshots);
// Completed envelopes are executed documents and cannot be deleted in-app.

export async function envVoid(envelope_id: string, reason?: string): Promise<void> {
  const { error } = await supabase.rpc('esign_void_envelope', {
    p_envelope_id: envelope_id,
    p_reason: reason ?? null,
  });
  if (error) throw new EsignError(400, error.message);
}

export async function envDelete(envelope_id: string, reason?: string): Promise<void> {
  const { error } = await supabase.rpc('esign_delete_envelope', {
    p_envelope_id: envelope_id,
    p_reason: reason ?? null,
  });
  if (error) throw new EsignError(400, error.message);
}

/** Employee-facing PDF (source pre-seal, sealed once Completed) → object URL. */
export async function fetchEnvelopePdf(envelope_id: string): Promise<string> {
  const jwt = getSupabaseToken();
  if (!jwt) throw new EsignError(401, 'not_signed_in');
  const res = await fetch(ESIGN_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${jwt}`,
    },
    body: JSON.stringify({ action: 'env_pdf', envelope_id }),
  });
  if (!res.ok) {
    const j = (await res.json().catch(() => ({}))) as { error?: string };
    throw new EsignError(res.status, j.error ?? 'pdf_fetch_failed');
  }
  return URL.createObjectURL(await res.blob());
}

// ── public (signer) actions ─────────────────────────────────────────────────

export function envSignerSign(input: {
  token: string;
  signed_name?: string;
  consent: boolean;
  values?: Record<string, string | boolean>;
  /** How the signature was created — recorded on the audit trail. */
  adoption_method?: 'typed' | 'drawn' | 'uploaded';
}): Promise<{ ok: boolean; signed_at: string; completed: boolean }> {
  return esignPublic({ action: 'sign', ...input });
}

/**
 * ESIGN/UETA consent gate. Called the moment the signer accepts, before the
 * document is unlocked, so the audit trail records consent and signature as
 * separate events with their own timestamps, IPs and user agents.
 */
export function envSignerConsent(
  token: string,
): Promise<{ ok: boolean; already: boolean }> {
  return esignPublic({ action: 'consent', token, consent: true });
}

/**
 * Report that the document was actually painted on the signer's screen.
 *
 * The trail already logs "viewed" when the signing page opens. That is a
 * weaker claim than it sounds: the mobile form view lets a signer complete
 * every field without the document ever being rendered. This is the honest
 * version of "they saw it", and it is fired only after pdf.js returns pages.
 */
export function envSignerDocumentShown(
  token: string,
): Promise<{ ok: boolean; already: boolean }> {
  return esignPublic({ action: 'document_shown', token });
}

// ── pdfjs rendering ─────────────────────────────────────────────────────────

export interface RenderedPage {
  pageNumber: number;
  canvas: HTMLCanvasElement;
  /** CSS pixel size the canvas is displayed at. */
  width: number;
  height: number;
}

/**
 * Extract every run of text from a PDF with NORMALISED boxes (0–1 of the
 * page), which is the coordinate space fields are stored in. Feeds
 * anchor-string placement — see anchor-placement.ts for the matching rules.
 *
 * pdf.js reports text in PDF user space: bottom-left origin, y increasing
 * upwards. Fields use a top-left origin with y increasing downwards, so the
 * y axis is flipped here once, at the boundary, rather than in the caller.
 */
export async function extractTextRuns(
  src: string | ArrayBuffer,
): Promise<Array<{
  text: string; x: number; y: number; width: number; height: number; page: number;
}>> {
  const doc = await pdfjsLib.getDocument(
    typeof src === 'string' ? { url: src } : { data: src },
  ).promise;
  const out: Array<{
    text: string; x: number; y: number; width: number; height: number; page: number;
  }> = [];
  for (let n = 1; n <= doc.numPages; n++) {
    const page = await doc.getPage(n);
    const vp = page.getViewport({ scale: 1 });
    const content = await page.getTextContent();
    for (const item of content.items) {
      if (!('str' in item) || !item.str.trim()) continue;
      const [, , , , e, f] = item.transform as number[];
      const h = item.height || 10;
      out.push({
        text: item.str,
        x: e / vp.width,
        y: (vp.height - f - h) / vp.height,
        width: item.width / vp.width,
        height: h / vp.height,
        page: n,
      });
    }
  }
  return out;
}

/**
 * Render pages AND extract text in ONE pdf.js document load.
 *
 * The prep screen previously opened the same PDF twice — once to render and
 * once to extract text for anchor placement and field detection. Two
 * concurrent `getDocument` calls on the same bytes is the prime suspect for
 * two failures Ben hit on 2026-07-29: the "Suggested fields" panel sitting
 * permanently greyed out (text extraction never resolved) and, separately, a
 * render that hung with no error. One load, one document, both outputs.
 *
 * Text extraction failing does NOT fail the whole call — rendering is the
 * thing the sender actually needs. The error is returned so the caller can
 * say so out loud instead of silently disabling the feature, which is what
 * the previous empty catch did.
 */
export async function loadPdfPagesAndText(
  src: string | ArrayBuffer,
  targetWidth: number,
): Promise<{
  pages: RenderedPage[];
  runs: Array<{
    text: string; x: number; y: number; width: number; height: number; page: number;
  }>;
  textError: string | null;
}> {
  const doc = await pdfjsLib.getDocument(
    typeof src === 'string' ? { url: src } : { data: src },
  ).promise;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const pages: RenderedPage[] = [];
  const runs: Array<{
    text: string; x: number; y: number; width: number; height: number; page: number;
  }> = [];
  let textError: string | null = null;

  for (let n = 1; n <= doc.numPages; n++) {
    const page = await doc.getPage(n);
    const base = page.getViewport({ scale: 1 });
    const scale = targetWidth / base.width;
    const viewport = page.getViewport({ scale: scale * dpr });
    const canvas = document.createElement('canvas');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    canvas.style.width = `${targetWidth}px`;
    canvas.style.height = `${(viewport.height / dpr).toFixed(1)}px`;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      await page.render({ canvasContext: ctx, viewport, canvas }).promise;
    }
    pages.push({
      pageNumber: n,
      canvas,
      width: targetWidth,
      height: viewport.height / dpr,
    });

    try {
      const content = await page.getTextContent();
      for (const item of content.items) {
        if (!('str' in item) || !item.str.trim()) continue;
        const [, , , , e, f] = item.transform as number[];
        const h = item.height || 10;
        runs.push({
          text: item.str,
          x: e / base.width,
          // pdf.js is bottom-left origin; fields are top-left. Flipped once,
          // here at the boundary, rather than in every caller.
          y: (base.height - f - h) / base.height,
          width: item.width / base.width,
          height: h / base.height,
          page: n,
        });
      }
    } catch (err) {
      textError = errorMessage(err, 'text_extraction_failed');
    }
  }

  return { pages, runs, textError };
}

/**
 * Render every page of a PDF (object URL or ArrayBuffer) to canvases sized
 * to `targetWidth` CSS px. Overlay coords are normalized against these sizes.
 */
export async function renderPdfPages(
  src: string | ArrayBuffer,
  targetWidth: number,
): Promise<RenderedPage[]> {
  const doc = await pdfjsLib.getDocument(
    typeof src === 'string' ? { url: src } : { data: src },
  ).promise;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const out: RenderedPage[] = [];
  for (let n = 1; n <= doc.numPages; n++) {
    const page = await doc.getPage(n);
    const base = page.getViewport({ scale: 1 });
    const scale = targetWidth / base.width;
    const viewport = page.getViewport({ scale: scale * dpr });
    const canvas = document.createElement('canvas');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    canvas.style.width = `${targetWidth}px`;
    canvas.style.height = `${(viewport.height / dpr).toFixed(1)}px`;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('canvas_2d_unavailable');
    await page.render({ canvas, canvasContext: ctx, viewport }).promise;
    out.push({
      pageNumber: n,
      canvas,
      width: targetWidth,
      height: viewport.height / dpr,
    });
  }
  return out;
}

// ── AFB-0075 / PR 4: the PDF's OWN form fields ──────────────────────────────
//
// Antonela's onboarding PDFs are not flat scans; they are AcroForms — 107
// declared widgets on the base packet, 154 on the Colorado one. Every box she
// would otherwise place by hand is already described by the document, so this
// reads the description instead of asking her to retype it.
//
// This half is deliberately DUMB. It loads the bytes, walks the pages, and
// copies each widget's declared properties into plain data. Every rule — the
// coordinate transform, the type mapping, the refusals, the sensitive-field
// ruling — lives in ./acroform-placement, which imports no pdfjs and is
// therefore the only one of the two a node test can load at all. If a decision
// could be got wrong, it belongs on that side of this boundary.

/** base64 (no data: prefix) → bytes, for handing a stored document to pdf.js. */
export function base64ToBytes(b64: string): Uint8Array {
  const binary = atob(b64);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) out[i] = binary.charCodeAt(i);
  return out;
}

// pdf.js types getAnnotations() as Promise<Array<any>>, so each entry is read
// through these rather than cast: an annotation that is missing a property, or
// carries a surprising type in one, must read as "absent" and not crash a scan
// of 154 widgets on the one that is odd.
function annString(a: Record<string, unknown>, key: string): string {
  const v = a[key];
  return typeof v === 'string' ? v : '';
}

function annFlag(a: Record<string, unknown>, key: string): boolean {
  return a[key] === true;
}

function annNumbers(a: Record<string, unknown>, key: string): number[] {
  const v = a[key];
  return Array.isArray(v) ? v.filter((n): n is number => typeof n === 'number') : [];
}

/**
 * Every /Widget annotation in a PDF, with the page geometry needed to place it.
 *
 * `intent: 'display'` is the intent the viewer itself uses, so what comes back
 * is what the document shows rather than a print-only variant of it.
 */
export async function extractAcroFormWidgets(
  src: string | ArrayBuffer | Uint8Array,
): Promise<{ pages: AcroFormPage[]; widgets: AcroFormWidget[] }> {
  const doc = await pdfjsLib.getDocument(
    typeof src === 'string' ? { url: src } : { data: src },
  ).promise;
  const pages: AcroFormPage[] = [];
  const widgets: AcroFormWidget[] = [];
  for (let n = 1; n <= doc.numPages; n++) {
    const page = await doc.getPage(n);
    const vp = page.getViewport({ scale: 1 });
    // viewBox, not width/height: the origin is not always [0,0] and the
    // transform subtracts it. rotation is carried so the pure side can refuse
    // a page rather than guess at it.
    pages.push({ page: n, viewBox: [...vp.viewBox], rotation: vp.rotation });
    const annotations: unknown[] = await page.getAnnotations({ intent: 'display' });
    for (const raw of annotations) {
      if (raw === null || typeof raw !== 'object') continue;
      const a = raw as Record<string, unknown>;
      if (annString(a, 'subtype') !== 'Widget') continue;
      widgets.push({
        page: n,
        fieldName: annString(a, 'fieldName'),
        fieldType: annString(a, 'fieldType'),
        rect: annNumbers(a, 'rect'),
        readOnly: annFlag(a, 'readOnly'),
        hidden: annFlag(a, 'hidden'),
        radioButton: annFlag(a, 'radioButton'),
        checkBox: annFlag(a, 'checkBox'),
        pushButton: annFlag(a, 'pushButton'),
      });
    }
  }
  return { pages, widgets };
}

/** The AcroForm of the PDF saved on a template, read from the stored bytes. */
export async function readTemplateAcroForm(
  template: EsignTemplate,
): Promise<{ pages: AcroFormPage[]; widgets: AcroFormWidget[] }> {
  return extractAcroFormWidgets(base64ToBytes(await loadTemplateDocumentBase64(template)));
}

/** File → base64 (no data: prefix), for env_create/env_send uploads. */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? '');
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.onerror = () => reject(new Error('read_failed'));
    reader.readAsDataURL(file);
  });
}

/** Stable per-recipient accent colors for field chips/boxes. */
export const RECIPIENT_COLORS = [
  '#2563eb', '#d97706', '#059669', '#dc2626', '#7c3aed', '#0891b2',
];
export function recipientColor(index: number): string {
  return RECIPIENT_COLORS[index % RECIPIENT_COLORS.length];
}

export const FIELD_DEFAULTS: Record<FieldType, { w: number; h: number; label: string }> = {
  signature: { w: 0.28, h: 0.045, label: 'Signature' },
  initials: { w: 0.09, h: 0.035, label: 'Initials' },
  name: { w: 0.22, h: 0.03, label: 'Name' },
  email: { w: 0.24, h: 0.03, label: 'Email' },
  date: { w: 0.13, h: 0.03, label: 'Date' },
  text: { w: 0.22, h: 0.03, label: 'Text' },
  checkbox: { w: 0.025, h: 0.02, label: 'Checkbox' },
};
